import React, { useState, type MouseEvent } from 'react';
import styled from 'styled-components';
import { Check, Copy } from '@phosphor-icons/react';
import { theme, ToastMessage } from '../utility';

const TextToCopy = styled.div`
  display: flex;
  align-items: center;

  .copy-icons.position-right svg,
  .copy-icons.position-right img {
    margin-left: 4px;
  }

  .copy-icons.position-left svg,
  .copy-icons.position-left img {
    margin-right: 4px;
  }

  .copy-icons svg,
  .copy-icons img {
    font-size: 120%;
  }

  .copy-icons {
    display: flex;
    align-items: center;
    opacity: 0;
    cursor: copy;
  }

  .icon {
    min-width: 16px;
  }

  &:hover {
    .copy-icons {
      opacity: 1;
    }
  }
`;

interface ITextCopyToClipboardProps {
  text: string;
  copiedText?: string;
  iconSize?: number;
  position?: 'left' | 'right';
  className?: string;
  copyText?: boolean;
  showText?: boolean;
  showIcon?: boolean;
  copyTextValue?: string;
}

export function TextCopyToClipboard({
  text,
  copiedText,
  iconSize = 16,
  position = 'right',
  className = '',
  copyText = true,
  showText = true,
  showIcon = true,
  copyTextValue = '',
}: ITextCopyToClipboardProps): JSX.Element {
  const [isCopied, setIsCopied] = useState<boolean>(false);

  async function copyTextToClipboard(): Promise<void | boolean> {
    if ('clipboard' in navigator) {
      if (copyTextValue.length) {
        return await navigator.clipboard.writeText(copyTextValue);
      }
      return await navigator.clipboard.writeText(text);
    }
    return document.execCommand('copy', true, text);
  }

  const handleCopyClick = (
    e: MouseEvent<HTMLDivElement>,
    preventCopy: boolean = true,
  ): void => {
    if (!preventCopy) return;

    e.stopPropagation();
    e.preventDefault();
    copyTextToClipboard()
      .then(() => {
        setIsCopied(true);
        setTimeout(() => {
          setIsCopied(false);
        }, 1500);
      })
      .catch(() => {
        ToastMessage.error('Something went wrong while copying.');
      });
  };

  const copyIcons = text ? (
    <div className={`copy-icons position-${position}`} onClick={e => handleCopyClick(e)}>
      {!isCopied && <Copy className="cursor-pointer icon" size={iconSize} />}
      {isCopied && (
        <Check
          className="cursor-pointer icon"
          style={{ color: theme.colors.success }}
          size={iconSize}
        />
      )}
    </div>
  ) : (
    <></>
  );

  return (
    <TextToCopy
      className={className}
      onClick={e => handleCopyClick(e, copyText)}
      onMouseLeave={() => setIsCopied(false)}
    >
      {showIcon && position === 'left' && copyIcons}
      {showText ? (copiedText && isCopied ? copiedText : text) : ''}
      {showIcon && position === 'right' && copyIcons}
    </TextToCopy>
  );
}

export default TextCopyToClipboard;
